import { Controller } from "@hotwired/stimulus"

// Phase 2.6 Tier 2 — Risk Mitigations panel controller. Owns the
// "+ Add Mitigation" modal (two modes: link existing / create new),
// optimistic DOM updates on add / mark-applied / rate-effectiveness /
// remove, and per-row error surfacing. Backed by RiskMitigationsController.
export default class extends Controller {
  static targets = [
    "modal", "modeTab", "createPane", "linkPane",
    "title", "targetDate", "responsible", "notes",
    "search", "searchResults", "errorBox", "submitBtn",
    "table", "rows", "row", "emptyState"
  ]

  static values = {
    createUrl: String,
    pickerUrl: String,
    defaultTargetDate: String,
    defaultResponsible: String
  }

  connect() {
    this.mode = "create"
    this._selectedPunchlistId = null
  }

  // ─── Modal management ────────────────────────────────────────────

  openModal() {
    this.modalTarget.classList.remove("hidden")
    this._switchMode("create")
    if (this.hasTitleTarget) this.titleTarget.focus()
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    this._reset()
  }

  hideModalOnBackdrop(event) {
    if (event.target === this.modalTarget) this.hideModal()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  switchMode(event) {
    this._switchMode(event.currentTarget.dataset.mode)
  }

  _switchMode(mode) {
    this.mode = mode
    this.modeTabTargets.forEach(tab => {
      const active = tab.dataset.mode === mode
      tab.classList.toggle("border-brand", active)
      tab.classList.toggle("text-brand", active)
      tab.classList.toggle("font-medium", active)
      tab.classList.toggle("border-transparent", !active)
      tab.classList.toggle("text-gray-500", !active)
    })
    if (this.hasCreatePaneTarget) this.createPaneTarget.classList.toggle("hidden", mode !== "create")
    if (this.hasLinkPaneTarget) this.linkPaneTarget.classList.toggle("hidden", mode !== "link")
    if (mode === "link") this._search()
  }

  _reset() {
    this._selectedPunchlistId = null
    if (this.hasTitleTarget) this.titleTarget.value = ""
    if (this.hasNotesTarget) this.notesTarget.value = ""
    if (this.hasSearchTarget) this.searchTarget.value = ""
    if (this.hasSearchResultsTarget) this.searchResultsTarget.innerHTML = ""
    if (this.hasErrorBoxTarget) {
      this.errorBoxTarget.classList.add("hidden")
      this.errorBoxTarget.textContent = ""
    }
    if (this.hasResponsibleTarget) this.responsibleTarget.value = this.defaultResponsibleValue || ""
    if (this.hasTargetDateTarget && this.defaultTargetDateValue) {
      this.targetDateTarget.value = this.defaultTargetDateValue
    }
  }

  // ─── Search (Link existing) ──────────────────────────────────────

  search() {
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => this._search(), 200)
  }

  async _search() {
    if (!this.hasSearchResultsTarget) return
    const q = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    const url = `${this.pickerUrlValue}?q=${encodeURIComponent(q)}`
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      const json = await res.json()
      this._renderSearchResults(json.rows || [])
    } catch (e) {
      this._renderSearchResults([])
    }
  }

  _renderSearchResults(rows) {
    if (rows.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="px-3 py-4 text-center text-[12px] text-gray-500">No matching action items.</div>`
      return
    }
    this.searchResultsTarget.innerHTML = rows.map(r => `
      <button type="button" class="w-full text-left px-3 py-2 hover:bg-gray-50 cursor-pointer"
              data-action="click->risk-mitigation#selectExisting"
              data-punchlist-id="${r.id}">
        <div class="text-[13px] text-gray-800">
          <span class="font-medium">${this._esc(r.code)}</span> — ${this._esc(r.title)}
        </div>
        <div class="text-[11px] text-gray-500 mt-0.5">
          Due ${this._esc(r.target_date || "—")}${r.responsible ? " · " + this._esc(r.responsible) : ""}
          ${r.completed ? " · Completed" : ""}
        </div>
      </button>`).join("")
  }

  selectExisting(event) {
    const id = event.currentTarget.dataset.punchlistId
    this._selectedPunchlistId = id
    this.searchResultsTarget.querySelectorAll("button").forEach(b => {
      b.classList.toggle("bg-brand/5", b.dataset.punchlistId === id)
      b.classList.toggle("ring-1", b.dataset.punchlistId === id)
      b.classList.toggle("ring-brand", b.dataset.punchlistId === id)
    })
  }

  // ─── Submit / row actions ────────────────────────────────────────

  async submit() {
    this._clearError()
    const fd = new FormData()
    if (this.mode === "link") {
      if (!this._selectedPunchlistId) {
        this._showError("Pick a Punchlist to link.")
        return
      }
      fd.append("existing_punchlist_id", this._selectedPunchlistId)
    } else {
      if (this.hasTitleTarget) fd.append("title", this.titleTarget.value)
      if (this.hasTargetDateTarget) fd.append("target_date", this.targetDateTarget.value)
      if (this.hasResponsibleTarget) fd.append("responsible_party_list", this.responsibleTarget.value)
    }
    if (this.hasNotesTarget) fd.append("notes", this.notesTarget.value)

    this.submitBtnTarget.disabled = true
    try {
      const json = await this._post(this.createUrlValue, fd)
      if (!json.ok) {
        this._showError((json.errors || ["Save failed"]).join(", "))
        return
      }
      this._appendRow(json.row)
      this._showToast("Mitigation added", "notice")
      this.hideModal()
      if (json.ready_to_resolve) location.reload()
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }

  async markApplied(event) {
    const id = event.currentTarget.dataset.mitigationId
    const row = this._rowFor(id)
    if (!row) return
    const fd = new FormData()
    fd.append("applied_at", new Date().toISOString())

    const json = await this._patch(row.dataset.updateUrl, fd)
    if (json.ok) {
      this._replaceRow(row, json.row)
      this._showToast("Mitigation marked applied", "notice")
      if (json.ready_to_resolve) location.reload()
    } else {
      this._showToast((json.errors || ["Update failed"]).join(", "), "alert")
    }
  }

  async rateEffectiveness(event) {
    const id = event.currentTarget.dataset.mitigationId
    const rating = event.currentTarget.value
    const row = this._rowFor(id)
    if (!row || !rating) return
    const fd = new FormData()
    fd.append("effectiveness_rating", rating)

    const json = await this._patch(row.dataset.updateUrl, fd)
    if (json.ok) {
      this._replaceRow(row, json.row)
      this._showToast("Effectiveness recorded", "notice")
    } else {
      this._showToast((json.errors || ["Update failed"]).join(", "), "alert")
    }
  }

  async remove(event) {
    const id = event.currentTarget.dataset.mitigationId
    const row = this._rowFor(id)
    if (!row) return

    const json = await this._delete(row.dataset.destroyUrl)
    if (json.ok) {
      row.remove()
      this._refreshEmptyState()
      this._showToast("Mitigation unlinked", "notice")
    } else {
      this._showToast("Unlink failed", "alert")
    }
  }

  // ─── DOM helpers ─────────────────────────────────────────────────

  _appendRow(row) {
    const tr = document.createElement("tr")
    tr.dataset.mitigationId = row.id
    tr.dataset.riskMitigationTarget = "row"
    tr.dataset.updateUrl = `${this.createUrlValue}/${row.id}`
    tr.dataset.destroyUrl = `${this.createUrlValue}/${row.id}`
    tr.className = "hover:bg-gray-50"
    tr.innerHTML = this._renderRowCells(row)
    this.rowsTarget.appendChild(tr)
    this._refreshEmptyState()
  }

  _replaceRow(row, payload) {
    row.innerHTML = this._renderRowCells(payload)
  }

  _renderRowCells(row) {
    const statusLabel = row.status_label || "In Progress"
    const statusClass = {
      "Applied": "bg-emerald-100 text-emerald-800",
      "Completed": "bg-blue-100 text-blue-800",
      "Approved": "bg-indigo-100 text-indigo-800"
    }[statusLabel] || "bg-gray-100 text-gray-700"

    const punchlist = row.punchlist_url
      ? `<a href="${row.punchlist_url}" target="_blank" class="text-brand hover:underline">
           <span class="font-medium">${this._esc(row.punchlist_code)}</span> — ${this._esc(row.punchlist_title || "")}
         </a>`
      : `<span class="text-rose-700">Action item deleted</span>`

    const effectiveness = row.applied
      ? (row.effectiveness_rating
          ? `<div class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold">${row.effectiveness_rating}</div>`
          : `<select data-action="change->risk-mitigation#rateEffectiveness"
                     data-mitigation-id="${row.id}"
                     class="px-2 py-0.5 text-[12px] border border-gray-200 rounded">
               <option value="">Rate 1-5</option>
               ${[1,2,3,4,5].map(n => `<option value="${n}">${n}</option>`).join("")}
             </select>`)
      : `<span class="text-gray-400 text-[12px]">—</span>`

    const actions = `${row.applied ? "" : `
      <button type="button" data-action="click->risk-mitigation#markApplied"
              data-mitigation-id="${row.id}"
              class="px-2 py-0.5 text-[12px] font-medium text-emerald-700 hover:bg-emerald-50 rounded cursor-pointer">
        Mark applied
      </button>`}
      <button type="button" data-action="click->risk-mitigation#remove"
              data-mitigation-id="${row.id}"
              class="ml-1 px-2 py-0.5 text-[12px] text-rose-700 hover:bg-rose-50 rounded cursor-pointer">
        Remove
      </button>`

    const notesHtml = row.notes
      ? `<div class="text-[11px] text-gray-500 mt-0.5">${this._esc(row.notes)}</div>`
      : ""
    const appliedAtHtml = row.applied_at
      ? `<div class="text-[11px] text-gray-500 mt-0.5">${this._esc(row.applied_at)}</div>`
      : ""

    return `
      <td class="px-2 py-1.5 text-gray-800">${punchlist}${notesHtml}</td>
      <td class="px-2 py-1.5 text-gray-600 whitespace-nowrap">${this._esc(row.target_date || "—")}</td>
      <td class="px-2 py-1.5 text-gray-600">${this._esc(row.responsible_party || "—")}</td>
      <td class="px-2 py-1.5">
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass}">${statusLabel}</span>
        ${appliedAtHtml}
      </td>
      <td class="px-2 py-1.5 text-center">${effectiveness}</td>
      <td class="px-2 py-1.5 text-right">${actions}</td>
    `
  }

  _rowFor(id) {
    return this.rowTargets.find(r => r.dataset.mitigationId === id) || null
  }

  _refreshEmptyState() {
    if (!this.hasEmptyStateTarget || !this.hasTableTarget) return
    const any = this.rowsTarget.querySelectorAll("tr").length > 0
    this.emptyStateTarget.classList.toggle("hidden", any)
    this.tableTarget.classList.toggle("hidden", !any)
  }

  // ─── Network helpers ─────────────────────────────────────────────

  async _post(url, fd) {
    const res = await fetch(url, {
      method: "POST",
      body: fd,
      headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" }
    })
    return res.json()
  }

  async _patch(url, fd) {
    fd.append("_method", "patch")
    return this._post(url, fd)
  }

  async _delete(url) {
    const res = await fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" }
    })
    return res.json()
  }

  _showError(msg) {
    if (!this.hasErrorBoxTarget) return
    this.errorBoxTarget.textContent = msg
    this.errorBoxTarget.classList.remove("hidden")
  }

  _clearError() {
    if (!this.hasErrorBoxTarget) return
    this.errorBoxTarget.classList.add("hidden")
    this.errorBoxTarget.textContent = ""
  }

  _showToast(message, type) {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.dataset.controller = "flash"
    toast.dataset.flashDismissAfterValue = "5000"
    toast.textContent = message
    document.body.prepend(toast)
  }

  _esc(s) {
    if (s == null) return ""
    return String(s).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ""
}
